/**
 * Web Audio API Synth for offline, bulletproof audio feedback.
 * Works without any external network dependency!
 */

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

/**
 * Play a light "click" woodblock note for virtual keyboard buttons or dragging
 */
export function playTick(): void {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.08);
  } catch (error) {
    console.warn('Audio synthesis failed:', error);
  }
}

/**
 * Play a soft warning beep for incorrect matches
 */
export function playBuzzer(): void {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(80, ctx.currentTime + 0.25);

    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.25);

    // Apply lowpass filter to make buzzer less abrasive
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(300, ctx.currentTime);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  } catch (error) {
    console.warn('Audio synthesis failed:', error);
  }
}

/**
 * Play an ascending, dual-tone angelic success chime for puzzle completion/unlocks
 */
export function playSuccessChime(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Harmonic chime 1 (warm)
    const playNote = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, start);
      
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(start);
      osc.stop(start + duration);
    };

    // Beautiful ascending major gold chord
    playNote(523.25, now, 0.4);       // C5
    playNote(659.25, now + 0.1, 0.45); // E5
    playNote(783.99, now + 0.2, 0.5);  // G5
    playNote(1046.50, now + 0.35, 0.65); // C6
  } catch (error) {
    console.warn('Audio synthesis failed:', error);
  }
}

/**
 * Play a grand fanfare trumpet blast for achievements!
 */
export function playFanfare(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const playTrumpet = (freq: number, start: number, duration: number, volume = 0.12) => {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      osc1.type = 'sawtooth';
      osc2.type = 'triangle';
      
      osc1.frequency.setValueAtTime(freq, start);
      osc2.frequency.setValueAtTime(freq + 3, start); // detune for brassy texture

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(1000, start);
      filter.frequency.linearRampToValueAtTime(2000, start + 0.1);

      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(volume, start + 0.05);
      gain.gain.linearRampToValueAtTime(volume * 0.8, start + duration - 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(start);
      osc1.stop(start + duration);
      osc2.start(start);
      osc2.stop(start + duration);
    };

    // Festive fanfare riff
    playTrumpet(392.00, now, 0.15);        // G4
    playTrumpet(392.00, now + 0.16, 0.15); // G4
    playTrumpet(392.00, now + 0.32, 0.15); // G4
    playTrumpet(523.25, now + 0.48, 0.4);  // C5
    playTrumpet(659.25, now + 0.88, 0.3);  // E5
    playTrumpet(783.99, now + 1.18, 0.6, 0.15); // G5 (sustained)
  } catch (error) {
    console.warn('Audio synthesis failed:', error);
  }
}
